import React, { useState, useEffect, useMemo, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import PassiveUxSignals from './components/PassiveUxSignals';
import useScrollUnlock from './hooks/useScrollUnlock';
import { AuthProvider } from './contexts/AuthContext';
import { SelectionProvider } from './contexts/SelectionContext';
import { ClientProvider } from './contexts/ClientContext';
import { ModalFocusProvider } from './contexts/ModalFocusContext';
import { GamificationProvider } from './contexts/GamificationContext';
import GlobalErrorBoundary from './components/GlobalErrorBoundary';
import OdvCentral from './layouts/OdvCentral';
import ParkingLot from './components/ParkingLot';
// RAG removed - unused system
import GoogleAuth from './components/GoogleAuth';
import LandingPage from './pages/marketing/LandingPage';
import ProLandingPage from './pages/marketing/ProLandingPage';
import Profile from './pages/Profile';
import Auth from './pages/auth';
import ResetPassword from './pages/auth-reset-password';
import { useAuth } from './contexts/AuthContext';
import LegacyEventQuoteLinkRedirect from './pages/onboarding/AcceptEventQuote.jsx';
import LegacyEventContractLinkRedirect from './pages/onboarding/Contract.jsx';
import LotAccessGuard from './components/guards/LotAccessGuard';
import AdminOnlyGuard from './components/guards/AdminOnlyGuard';
import CouncilOnlyGuard from './components/guards/CouncilOnlyGuard';
import ValetAuthGuard from './components/guards/ValetAuthGuard';
import { lazyWithRetry } from './utils/lazyWithRetry';

const PortalEntry = lazyWithRetry(() => import('@/lots/client-portal/vehicles/ClientPortalEntry'), 'PortalEntry');
const PortalShell = lazyWithRetry(() => import('@/lots/client-portal/vehicles/PortalShell'), 'PortalShell');
const InvoiceCheckoutPage = lazyWithRetry(() => import('@/lots/client-portal/vehicles/InvoiceCheckoutPage'), 'InvoiceCheckoutPage');
const InvoicePrintView = lazyWithRetry(() => import('@/lots/client-portal/vehicles/InvoicePrintView'), 'InvoicePrintView');
const ClientPortalSignup = lazyWithRetry(() => import('@/lots/client-portal/vehicles/ClientPortalSignup'), 'ClientPortalSignup');
const ClientLogin = lazyWithRetry(() => import('@/lots/client-portal/vehicles/ClientLogin'), 'ClientLogin');
// Note: PortalShell handles all client portal content internally (lazy loaded there)
// Join form lives on website (opendoorvalet.com/join) — redirect from Truest
const JoinRedirect = () => { window.location.href = 'https://opendoorvalet.com/join'; return null; };
const GseatProTeaser = lazyWithRetry(() => import('@/lots/valet-mobile/vehicles/GseatProTeaser'), 'GseatProTeaser');
const ValetShell = lazyWithRetry(() => import('@/lots/valet-mobile/components/ValetShell'), 'ValetShell');
const ValetPasswordSetup = lazyWithRetry(() => import('@/lots/valet-mobile/vehicles/ValetPasswordSetup'), 'ValetPasswordSetup');
const ValetDashboard = lazyWithRetry(() => import('./pages/valet/ValetDashboard'), 'ValetDashboard');
const ValetOnboarding = lazyWithRetry(() => import('./pages/valet/ValetOnboarding'), 'ValetOnboarding');

const ClaimPortalRedirect = lazyWithRetry(() => import('@/lots/client-portal/vehicles/ClaimPortalRedirect'), 'ClaimPortalRedirect');
const QuickAcceptAssignment = lazyWithRetry(() => import('@/lots/assignments/vehicles/QuickAcceptAssignment'), 'QuickAcceptAssignment');
const QuickAcceptSuccess = lazyWithRetry(() => import('@/lots/assignments/vehicles/QuickAcceptSuccess'), 'QuickAcceptSuccess');
const SubcontractorEventSignup = lazyWithRetry(() => import('@/lots/subcontracted/vehicles/SubcontractorEventSignup'), 'SubcontractorEventSignup');
const ODVWorld = lazyWithRetry(() => import('@/lots/world/vehicles/ODVWorld'), 'ODVWorld');
const ContractSigningPage = lazyWithRetry(() => import('@/pages/public/ContractSigningPage'), 'ContractSigningPage');
const CompanySignaturePage = lazyWithRetry(() => import('@/pages/public/CompanySignaturePage'), 'CompanySignaturePage');
const NewSignatureRequestPage = lazyWithRetry(() => import('@/pages/public/NewSignatureRequestPage'), 'NewSignatureRequestPage');
const SMSConsentPage = lazyWithRetry(() => import('@/pages/public/sms-consent'), 'SMSConsentPage');
const GSeatTerms = lazyWithRetry(() => import('@/pages/public/GSeatTerms'), 'GSeatTerms');
const GSeatPrivacy = lazyWithRetry(() => import('@/pages/public/GSeatPrivacy'), 'GSeatPrivacy');
const ProposalPublicPage = lazyWithRetry(() => import('@/pages/public/ProposalPublicPage'), 'ProposalPublicPage');
const ReportEmailPreferencePage = lazyWithRetry(() => import('@/pages/public/ReportEmailPreferencePage'), 'ReportEmailPreferencePage');
const EventJourneyPage = lazyWithRetry(() => import('@/pages/public/EventJourneyPage'), 'EventJourneyPage');
const CustomerTicketPage = lazyWithRetry(() => import('@/pages/ticket/CustomerTicketPage'), 'CustomerTicketPage');
const TipPage = lazyWithRetry(() => import('@/pages/ticket/TipPage'), 'TipPage');
// Front-desk tablet board (Doug 2026-07-29). DEMO: dev-only until the desk_stations
// device-enrollment gate lands — see the header comment in FrontDeskBoard.jsx.
const FrontDeskBoard = lazyWithRetry(() => import('@/lots/front-desk/FrontDeskBoard'), 'FrontDeskBoard');
// Walk-In location SOPs on the web — the phone surfaces need a Mac build; this
// deploys from any machine. Same sops_api writer, so the two cannot disagree.
const WalkInBoard = lazyWithRetry(() => import('@/lots/locations/components/WalkInBoard'), 'WalkInBoard');
const PublicShuttleBooking = lazyWithRetry(() => import('@/lots/shuttle/vehicles/PublicShuttleBooking'), 'PublicShuttleBooking');

// Certification - Hook Experience (token-based, public)
const HookExperience = lazyWithRetry(() => import('@/lots/certification/vehicles/HookExperience'), 'HookExperience');
const StageConfirmation = lazyWithRetry(() => import('@/lots/certification/components/StageConfirmation'), 'StageConfirmation');

// Recruit self-service scheduling (token-based, public)
const ScheduleStage = lazyWithRetry(() => import('@/pages/join/ScheduleStage'), 'ScheduleStage');
const StagingPlaybook = lazyWithRetry(() => import('@/pages/join/StagingPlaybook'), 'StagingPlaybook');
const TrainingInfo = lazyWithRetry(() => import('@/pages/join/TrainingInfo'), 'TrainingInfo');

// Demo pages (direct route, bypass ParkingLot)
const GamificationDemo = lazyWithRetry(() => import('@/pages/GamificationDemo'), 'GamificationDemo');
const TeslaCallback = lazyWithRetry(() => import('@/lots/tesla/TeslaCallback'), 'TeslaCallback');
const BCPRecoveryDashboard = lazyWithRetry(() => import('@/pages/BCPRecoveryDashboard'), 'BCPRecoveryDashboard');
const OutreachCampaigns = lazyWithRetry(() => import('@/pages/OutreachCampaigns'), 'OutreachCampaigns');

// Valet fetching your vehicle
const ValetFetching = () => (
  <div className="flex items-center justify-center h-full">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-vegas-gold mx-auto mb-4"></div>
      <p className="text-gray-400">Valet fetching your vehicle...</p>
    </div>
  </div>
);

// Inner app component with auth access
const AppRoutes = () => {
  const { user, loading, authReady, hasDashboardAccess, valetProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showGoogleAuth, setShowGoogleAuth] = useState(false);
  const [attemptedPath, setAttemptedPath] = useState(null);

  // Expose navigate for ScreenAutomationService (client-side navigation without page reload)
  useEffect(() => {
    window.__ODV_NAVIGATE__ = navigate;
    return () => {
      delete window.__ODV_NAVIGATE__;
    };
  }, [navigate]);

  // Unlock scrolling on all public/client-facing routes (global overflow:hidden blocks them)
  const PUBLIC_PREFIXES = ['/portal', '/invoice', '/sign', '/event-contract', '/accept-event-quote', '/ticket', '/tip', '/sub', '/valet', '/join', '/event/', '/p/', '/sms-consent', '/terms', '/privacy', '/certification/hook', '/quick-accept', '/landing', '/desk'];
  const isPublicPage = useMemo(() => PUBLIC_PREFIXES.some(p => location.pathname.startsWith(p)), [location.pathname]);
  useScrollUnlock(isPublicPage);

  // Handle session expiration and unauthorized access
  // CRITICAL: Wait for authReady before redirecting to avoid race condition during OAuth flow
  useEffect(() => {
    // Don't redirect until auth processing is complete
    if (!authReady) {
      console.log('[App] Waiting for auth to complete...');
      return;
    }

    if (!loading && !user) {
      const isPublicRoute = location.pathname.startsWith('/landing') ||
                           location.pathname.startsWith('/portal') ||
                           location.pathname.startsWith('/invoice') ||
                           location.pathname.startsWith('/join') ||
                           location.pathname.startsWith('/auth') ||
                           location.pathname.startsWith('/valet') ||
                           location.pathname.startsWith('/accept-event-quote') ||
                           location.pathname.startsWith('/event-contract') ||
                           location.pathname.startsWith('/quick-accept') ||
                           location.pathname.startsWith('/sub') ||
                           location.pathname.startsWith('/sign') ||
                           location.pathname.startsWith('/ticket') ||
                           location.pathname.startsWith('/tip') ||
                           location.pathname.startsWith('/sms-consent') ||
                           location.pathname.startsWith('/privacy') ||
                           location.pathname.startsWith('/terms') ||
                           location.pathname.startsWith('/certification/hook') ||
                           // A front-desk tablet has no ODV account by design. It
                           // authenticates with a desk_stations token it holds itself,
                           // and FrontDeskBoard shows the enrollment screen until it
                           // has one — so reaching this route session-less is correct,
                           // not a hole. See 20260804160000_desk_stations_and_desk_api.
                           location.pathname.startsWith('/desk') ||
                           location.pathname.startsWith('/p/') ||
                           location.pathname.startsWith('/event/') ||
                           location.pathname.startsWith('/tesla/');

      if (!isPublicRoute) {
        // Save attempted path for redirect after login
        setAttemptedPath(location.pathname);
        console.log('🚫 Session expired or unauthorized access to:', location.pathname);
        console.log('📍 Redirecting to login...');

        // Client subdomain → client login. Everything else → ops login.
        const authPath = window.location.hostname.startsWith('client.') ? '/portal/login' : '/auth';
        navigate(authPath, { replace: true });
      }
    }
  }, [user, loading, authReady, location.pathname, navigate]);

  // Redirect to attempted path after successful login
  useEffect(() => {
    if (user && attemptedPath) {
      console.log('✅ Login successful, redirecting to:', attemptedPath);
      navigate(attemptedPath, { replace: true });
      setAttemptedPath(null);
    }
  }, [user, attemptedPath, navigate]);

  // Check if current route is public (no auth required)
  const isLandingRoute = location.pathname.startsWith('/landing');
  const isPortalRoute = location.pathname.startsWith('/portal');
  const isInvoiceRoute = location.pathname.startsWith('/invoice');
  const isJoinRoute = location.pathname.startsWith('/join');
  const isAuthRoute = location.pathname.startsWith('/auth');
  const isValetRoute = location.pathname.startsWith('/valet');
  const isAcceptQuoteRoute = location.pathname.startsWith('/accept-event-quote');
  const isContractRoute = location.pathname.startsWith('/event-contract');
  const isQuickAcceptRoute = location.pathname.startsWith('/quick-accept');
  const isSubRoute = location.pathname.startsWith('/sub');
  const isSignRoute = location.pathname.startsWith('/sign');
  const isTicketRoute = location.pathname.startsWith('/ticket');
  const isTipRoute = location.pathname.startsWith('/tip');
  const isTermsRoute = location.pathname.startsWith('/terms');
  const isPrivacyRoute = location.pathname.startsWith('/privacy');
  const isCertHookRoute = location.pathname.startsWith('/certification/hook');
  const isProposalRoute = location.pathname.startsWith('/p/');
  const isEventJourneyRoute = location.pathname.startsWith('/event/');

  // If not authenticated AND not on public routes, redirect to /auth
  // This allows the dedicated Auth page to handle all auth UI
  if (!user && !isLandingRoute && !isPortalRoute && !isInvoiceRoute && !isJoinRoute && !isAuthRoute && !isValetRoute && !isAcceptQuoteRoute && !isContractRoute && !isQuickAcceptRoute && !isSubRoute && !isSignRoute && !isTicketRoute && !isTipRoute && !isTermsRoute && !isPrivacyRoute && !isCertHookRoute && !isProposalRoute && !isEventJourneyRoute) {
    // Show loading spinner if still loading OR if auth is processing (OAuth exchange)
    if (loading || !authReady) {
      return (
        <div className="flex items-center justify-center h-screen bg-black">
          <div className="text-center">
            <img
              src="/odv-logo-car.png"
              alt="ODV Logo"
              className="w-32 h-auto mx-auto mb-6 animate-pulse"
            />
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#AEA36D] mx-auto mb-3"></div>
            <p className="text-gray-500">Checking authentication...</p>
          </div>
        </div>
      );
    }
    // Not loading, auth ready, and not authenticated = navigate to auth (handled by useEffect above)
    return null;
  }

  return (
    <>
      {/* Google Auth Component */}
      <GoogleAuth
        isOpen={showGoogleAuth}
        onClose={() => setShowGoogleAuth(false)}
      />

      <Routes>
        {/* Root path - redirect based on auth status */}
        <Route 
          path="/" 
          element={
            loading ? (
              <div className="flex items-center justify-center h-screen bg-black">
                <div className="text-center">
                  <img
                    src="/odv-logo-car.png"
                    alt="ODV Logo"
                    className="w-32 h-auto mx-auto mb-6 animate-pulse"
                  />
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#AEA36D] mx-auto mb-3"></div>
                  <p className="text-gray-500">Loading...</p>
                </div>
              </div>
            ) : user ? (
              // Route based on user type:
              // 1. Client portal users → portal (metadata set during portal signup)
              // 2. Ops users with dashboard badges → ops dashboard
              // 3. Valets without dashboard badges → valet app
              // 4. Unknown users (no valet record, no badges, no portal) → auth
              user.user_metadata?.portal_access || user.user_metadata?.role === 'client'
                ? <Navigate to="/portal/dashboard" replace />
                : hasDashboardAccess
                  ? <Navigate to="/operations/assignments" replace />
                  : valetProfile
                    ? <Navigate to="/valet" replace />
                    : <Navigate to="/auth" replace />
            ) : (
              // Client subdomain → client login. Everything else → ops login.
              window.location.hostname.startsWith('client.')
                ? <Navigate to="/portal/login" replace />
                : <Navigate to="/auth" replace />
            )
          }
        />
        
        {/* Marketing Landing Pages - Public Routes */}
        <Route path="/landing" element={<ProLandingPage />} />
        <Route path="/landing-full" element={<LandingPage />} />
        <Route path="/landing-pro" element={<ProLandingPage />} />
        {/* Retired public UUID routes. These components never resolve or forward
            record IDs; current links live on the exact-token website routes. */}
        <Route path="/accept-event-quote/:eventId" element={<LegacyEventQuoteLinkRedirect />} />
        <Route path="/event-contract/:contractId" element={<LegacyEventContractLinkRedirect />} />
        <Route path="/tesla/callback" element={<Suspense fallback={<ValetFetching />}><TeslaCallback /></Suspense>} />

        <Route path="/terms" element={<Suspense fallback={<ValetFetching />}><GSeatTerms /></Suspense>} />
        <Route path="/privacy" element={<Suspense fallback={<ValetFetching />}><GSeatPrivacy /></Suspense>} />

        {/* Dynamic Proposal Portal - Public Route (pitch pages from data) */}
        <Route
          path="/p/:slug"
          element={
            <Suspense fallback={<ValetFetching />}>
              <ProposalPublicPage />
            </Suspense>
          }
        />

        {/* SMS Consent Policy - Public Route (For Twilio Toll-Free Verification) */}
        <Route
          path="/report-emails"
          element={
            <Suspense fallback={<ValetFetching />}>
              <ReportEmailPreferencePage />
            </Suspense>
          }
        />
        <Route
          path="/sms-consent"
          element={
            <Suspense fallback={<ValetFetching />}>
              <SMSConsentPage />
            </Suspense>
          }
        />

        {/* Contract Signing - Public Route (No Authentication Required) */}
        <Route
          path="/sign/:token"
          element={
            <Suspense fallback={<ValetFetching />}>
              <ContractSigningPage />
            </Suspense>
          }
        />

        {/* Company-side signature (2026-10-13): council signs while logged in; an outside signer by ?t=<token>.
            Who may sign is decided server-side (sign-company-document + contracts_api). */}
        <Route
          path="/sign-document/new"
          element={
            <Suspense fallback={<ValetFetching />}>
              <NewSignatureRequestPage />
            </Suspense>
          }
        />
        <Route
          path="/sign-document/:requestId"
          element={
            <Suspense fallback={<ValetFetching />}>
              <CompanySignaturePage />
            </Suspense>
          }
        />

        {/* Event Journey Page - Public Route (client lifecycle URL, no auth required) */}
        <Route
          path="/event/:token"
          element={
            <Suspense fallback={<ValetFetching />}>
              <EventJourneyPage />
            </Suspense>
          }
        />

        {/* Customer Ticket Page - Public Route (QR code access) */}
        <Route
          path="/ticket/:ticketId"
          element={
            <Suspense fallback={<ValetFetching />}>
              <CustomerTicketPage />
            </Suspense>
          }
        />

        {/* Front-Desk Tablet Board — the hotel's own surface.
            Session-less by design: a desk tablet authenticates with a `desk_stations`
            token it enrolled with (venue code, typed once), NOT an ODV account. The
            board renders its enrollment screen until it holds one, and every read and
            write goes through desk_api SECURITY DEFINER RPCs scoped to that station's
            location — anon has no grant on `tickets`, so money and coordinates are
            unreachable from the browser rather than merely un-selected.
            Revoke a device: UPDATE desk_stations SET revoked_at = now(). */}
        <Route
          path="/desk"
          element={
            <Suspense fallback={<ValetFetching />}>
              <FrontDeskBoard />
            </Suspense>
          }
        />

        {/* Know the Spot — author and publish the Walk-In location SOP from any
            machine. Writes go through sops_api (badge-gated in the DB), so this
            surface can be open without being a second writer. */}
        <Route
          path="/walk-in"
          element={
            <Suspense fallback={<ValetFetching />}>
              <WalkInBoard />
            </Suspense>
          }
        />

        {/* Tip Page - Public Route */}
        <Route
          path="/tip/:ticketId"
          element={
            <Suspense fallback={<ValetFetching />}>
              <TipPage />
            </Suspense>
          }
        />

        {/* Public Shuttle Booking - shareable link, accepts pre-fill query params */}
        <Route
          path="/shuttle/booking"
          element={
            <Suspense fallback={<ValetFetching />}>
              <PublicShuttleBooking />
            </Suspense>
          }
        />
        <Route
          path="/shuttle/book"
          element={
            <Suspense fallback={<ValetFetching />}>
              <PublicShuttleBooking />
            </Suspense>
          }
        />

        {/* Certification Hook Experience - Token-based Public Route */}
        <Route
          path="/certification/hook/:token"
          element={
            <Suspense fallback={<ValetFetching />}>
              <HookExperience />
            </Suspense>
          }
        />

        {/* Authentication - Public Routes */}
        <Route path="/auth" element={<Auth />} />
        <Route path="/auth/reset-password" element={<ResetPassword />} />

        {/* Recruiting - Public Route (No Authentication Required) */}
        <Route
          path="/join"
          element={
            <Suspense fallback={<ValetFetching />}>
              <JoinRedirect />
            </Suspense>
          }
        />

        {/* Recruit Self-Service Scheduling - Token-based Public Route */}
        <Route
          path="/join/schedule/:token"
          element={
            <Suspense fallback={<ValetFetching />}>
              <ScheduleStage />
            </Suspense>
          }
        />

        {/* Staging Playbook - Public Route */}
        <Route
          path="/join/playbook"
          element={
            <Suspense fallback={<ValetFetching />}>
              <StagingPlaybook />
            </Suspense>
          }
        />

        {/* Training Info - Public Route (D1/D2 training content) */}
        <Route
          path="/join/training/:stageType/:assignmentId"
          element={
            <Suspense fallback={<ValetFetching />}>
              <TrainingInfo />
            </Suspense>
          }
        />

        {/* /valet — web dashboard for signed-in valets, app downloads for everyone else.
            Doug 2026-08-20: "lets re open /valet for web dash access."

            It cannot simply become the dashboard: /valet is a PUBLIC route (it sits
            among /join/* and /quick-accept/:token) and GseatProTeaser is the ONLY
            remaining home for the APK + TestFlight links since System Config was
            deleted. Logged-out visitors — recruits sent here to install the app —
            must still land on the download page.

            ValetDashboard existed all along but was imported at the top of this file
            and never routed, so the valet nav's "Dashboard" entry (garages.js:200,
            path /valet) led to the teaser instead. */}
        <Route
          path="/valet"
          element={
            <Suspense fallback={<ValetFetching />}>
              {authReady && user
                ? <ValetShell><ValetDashboard /></ValetShell>
                : <GseatProTeaser />}
            </Suspense>
          }
        />

        {/* Signed-in valets still need a reachable native-app installer. Keep
            this separate from /valet so restoring dashboard access never hides
            TestFlight / Google Play again. ?stay=1 is used by dashboard links
            when the valet should see both platform choices. */}
        <Route
          path="/valet/get-app"
          element={
            <Suspense fallback={<ValetFetching />}>
              <GseatProTeaser />
            </Suspense>
          }
        />

        {/* Quick Accept Assignment - Public Route for Subcontracted Valets */}
        <Route
          path="/quick-accept/:token"
          element={
            <Suspense fallback={<ValetFetching />}>
              <QuickAcceptAssignment />
            </Suspense>
          }
        />
        <Route
          path="/quick-accept/success"
          element={
            <Suspense fallback={<ValetFetching />}>
              <QuickAcceptSuccess />
            </Suspense>
          }
        />

        {/* Subcontractor Event Signup - Public Route for temporary valets */}
        <Route
          path="/sub/:eventToken"
          element={
            <Suspense fallback={<ValetFetching />}>
              <SubcontractorEventSignup />
            </Suspense>
          }
        />

        {/* Client Portal - Public Routes (No Authentication Required) */}
        <Route
          path="/portal"
          element={
            <Suspense fallback={<ValetFetching />}>
              <PortalEntry />
            </Suspense>
          }
        />
        {/* Printable invoice view — opens in new tab for PDF download */}
        <Route
          path="/invoice/print"
          element={
            <Suspense fallback={<ValetFetching />}>
              <InvoicePrintView />
            </Suspense>
          }
        />
        {/* NEW: Streamlined checkout page - payment-first experience */}
        <Route
          path="/invoice"
          element={
            <Suspense fallback={<ValetFetching />}>
              <InvoiceCheckoutPage />
            </Suspense>
          }
        />
        {/* Path-param form of the same page. Emailed "View & Pay" links use
            /invoice/<id>?token=... — without this route they fell through to the
            garage catch-all (/:garageId/:lotId) → "Garage Not Found". (2026-06-23) */}
        <Route
          path="/invoice/:invoiceId"
          element={
            <Suspense fallback={<ValetFetching />}>
              <InvoiceCheckoutPage />
            </Suspense>
          }
        />
        <Route
          path="/portal/checkout"
          element={
            <Suspense fallback={<ValetFetching />}>
              <InvoiceCheckoutPage />
            </Suspense>
          }
        />
        <Route
          path="/portal/invoice"
          element={
            <Suspense fallback={<ValetFetching />}>
              {/* Compatibility route for older emailed links. Keep one complete,
                  payment-first invoice experience instead of resurrecting the
                  retired mini portal with parallel schedule/sales readers. */}
              <InvoiceCheckoutPage />
            </Suspense>
          }
        />
        <Route
          path="/portal/login"
          element={
            <Suspense fallback={<ValetFetching />}>
              <ClientLogin />
            </Suspense>
          }
        />
        <Route
          path="/portal/signup"
          element={
            <Suspense fallback={<ValetFetching />}>
              <ClientPortalSignup />
            </Suspense>
          }
        />
        <Route
          path="/portal/claim"
          element={
            <Suspense fallback={<ValetFetching />}>
              <ClaimPortalRedirect />
            </Suspense>
          }
        />

        {/* Client Portal - Single shell route for all authenticated tabs.
            Previously 8 separate <Route> entries each mounting a new PortalShell,
            causing full teardown+rebuild+7-8 API calls on every tab click. */}
        <Route
          path="/portal/:tab/*"
          element={
            <Suspense fallback={<ValetFetching />}>
              <PortalShell />
            </Suspense>
          }
        />

        {/* Main App Routes - All wrapped in OdvCentral layout + LotAccessGuard */}
        {/* The ParkingLot component now handles all authenticated routing dynamically */}
        <Route
          path="/*" // Catch-all for the main app - PROTECTED by LotAccessGuard
          element={
            <LotAccessGuard>
              <OdvCentral onShowAuth={() => setShowGoogleAuth(true)}>
                <Routes>
                  <Route path="/profile" element={<Profile />} />

                  {/* Specific routes for BUSINESS_ROUTES paths */}
                  <Route path="/valets/*" element={<ParkingLot />} />
                  <Route path="/assignments/*" element={<ParkingLot />} />
                  <Route path="/clients/*" element={<ParkingLot />} />
                  <Route path="/finance/*" element={<ParkingLot />} />
                  <Route path="/events/*" element={<ParkingLot />} />
                  <Route path="/locations/*" element={<ParkingLot />} />
                  <Route path="/scheduler/*" element={<ParkingLot />} />
                  <Route path="/payroll/*" element={<ParkingLot />} />
                  <Route path="/admin/*" element={<ParkingLot />} />
                  <Route path="/intel/*" element={<ParkingLot />} />
                  <Route path="/branding/*" element={<ParkingLot />} />
                  <Route path="/crm/*" element={<ParkingLot />} />
                  <Route path="/marketing/*" element={<ParkingLot />} />
                  <Route path="/leads/*" element={<ParkingLot />} />
                  <Route path="/hr/*" element={<ParkingLot />} />
                  <Route path="/recruiting/*" element={<ParkingLot />} />
                  <Route path="/knowledge-base/*" element={<ParkingLot />} />
                  <Route path="/comms/*" element={<ParkingLot />} />
                  <Route path="/sms/*" element={<ParkingLot />} />
                  <Route path="/partners/*" element={<ParkingLot />} />
                  {/* ODV World - MUD-style terminal interface */}
                  <Route
                    path="/operations/world"
                    element={
                      <Suspense fallback={<ValetFetching />}>
                        <ODVWorld />
                      </Suspense>
                    }
                  />
                  {/* Gamification Demo - Direct route bypassing ParkingLot */}
                  <Route
                    path="/demo/gamification"
                    element={
                      <Suspense fallback={<ValetFetching />}>
                        <GamificationDemo />
                      </Suspense>
                    }
                  />
                  {/* BCP Payment Recovery — live dashboard, direct route bypassing ParkingLot */}
                  <Route
                    path="/bcp-recovery"
                    element={
                      <Suspense fallback={<ValetFetching />}>
                        <BCPRecoveryDashboard />
                      </Suspense>
                    }
                  />
                  {/* Outreach campaigns (lost-key searches) — renders get_outreach_campaign(). 2026-09-15 */}
                  <Route path="/campaigns" element={<Suspense fallback={<ValetFetching />}><OutreachCampaigns /></Suspense>} />
                  <Route path="/campaigns/:id" element={<Suspense fallback={<ValetFetching />}><OutreachCampaigns /></Suspense>} />
                  {/* Dynamic routes as fallback */}
                  <Route path="/:garageId/:lotId" element={<ParkingLot />} />
                  <Route path="/:garageId" element={<ParkingLot />} />
                  <Route path="*" element={
                    <div style={{ padding: '60px 40px', textAlign: 'center', color: '#6B6A63' }}>
                      <p style={{ fontSize: '20px', fontWeight: 600, marginBottom: '8px', color: '#2E2C27' }}>Page not found</p>
                      <p style={{ fontSize: '14px', marginBottom: '24px' }}>The section you're looking for doesn't exist or has moved.</p>
                      <a href="/" style={{ color: '#aea36d', textDecoration: 'underline', fontSize: '14px' }}>← Return to Dashboard</a>
                    </div>
                  } />
                </Routes>
              </OdvCentral>
            </LotAccessGuard>
          }
        />
      </Routes>

      {/* No page-verdict pill (Doug LOCK 2026-10-03: never interrupt the UI).
          Passive signals only (no UI, signed-in only); explicit feedback is the
          "Send feedback" item in the profile menu. */}
      <PassiveUxSignals />
    </>
  );
};

const App = () => {
  return (
    <GlobalErrorBoundary>
      <Router>
        <AuthProvider>
          <GamificationProvider>
            <SelectionProvider>
              <ClientProvider>
                <ModalFocusProvider>
                  <AppRoutes />
                </ModalFocusProvider>
              </ClientProvider>
            </SelectionProvider>
          </GamificationProvider>
        </AuthProvider>
        {/* Toast Notifications */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#AEA36D',
                secondary: '#fff',
              },
            },
            error: {
              duration: 5000,
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />
      </Router>
    </GlobalErrorBoundary>
  );
};

export default App;
